// src/services/api.js

const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY || '{VITE_API_KEY}';
const UPLOAD_EMAIL = import.meta.env.VITE_UPLOAD_EMAIL || '{VITE_UPLOAD_EMAIL}';
const UPLOAD_PASSWORD = import.meta.env.VITE_UPLOAD_PASSWORD || '{VITE_UPLOAD_PASSWORD}';

export const NOCOBASE_BASE_URL = 'https://performance.autochek.africa';
export const NOCOBASE_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjYsInJvbGVOYW1lIjoiYWRtaW4iLCJpYXQiOjE3ODQ1NDA5NDgsImV4cCI6MzMzNDIxNDA5NDh9.6IGdF_--eZd42VE00dsaAOibgm0vaTnO22MlqWkP_4E';

/**
 * Generic fetch wrapper for API calls.
 * @param {string} endpoint - The API endpoint
 * @param {object} options  - Fetch options (method, body, headers, etc.)
 * @returns {Promise<any>} Parsed JSON response
 */
export async function apiClient(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;

  const hasAuth =
    options.headers &&
    (options.headers['Authorization'] || options.headers['authorization']);

  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(!hasAuth && API_KEY ? { 'X-API-Key': API_KEY } : {}),
  };

  const response = await fetch(url, {
    ...options,
    headers: { ...defaultHeaders, ...options.headers },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `API Error: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
}

/**
 * Authenticates a user and returns tokens.
 * @param {string} username
 * @param {string} password
 * @returns {Promise<{access_token: string, refresh_token: string, user: object}>}
 */
export async function login(username, password) {
  const response = await fetch(`${BASE_URL}/v2/users/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `Auth failed: ${response.status}`);
  }

  const resBody = await response.json();

  const accessToken =
    resBody.access_token ||
    resBody.data?.access_token ||
    resBody.data?.accessToken;
  const refreshToken =
    resBody.refresh_token ||
    resBody.data?.refresh_token ||
    resBody.data?.refreshToken;

  if (!accessToken) {
    throw new Error('No access token received from login');
  }

  return { access_token: accessToken, refresh_token: refreshToken, user: resBody.data };
}

/**
 * Registers a new user.
 * @param {object} userData
 * @returns {Promise<any>}
 */
export async function signup(userData) {
  const response = await fetch(`${BASE_URL}/v2/users/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `Signup failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Enables a newly-created user account.
 * @param {string} userId
 * @returns {Promise<any>}
 */
export async function enableUser(userId) {
  const response = await fetch(`${BASE_URL}/v2/users/enable`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.message || `Enable user failed: ${response.status}`
    );
  }

  return response.json();
}

/**
 * Requests a password-reset code to be sent to the user.
 * @param {string} username - The user's email
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function forgotPassword(username) {
  const response = await fetch(`${BASE_URL}/v2/users/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.message || `Forgot password failed: ${response.status}`,
    );
  }

  return response.json();
}

/**
 * Resets the user's password with a verification code.
 * @param {string} code            - The reset code from the user's email
 * @param {string} identifier      - The user's email address
 * @param {string} newPassword     - The desired new password
 * @param {string} confirmPassword - Confirm new password
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function resetPassword(code, identifier, newPassword, confirmPassword) {
  const response = await fetch(`${BASE_URL}/v2/users/passwords/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      identifier,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.message || `Password reset failed: ${response.status}`,
    );
  }

  return response.json();
}

/**
 * Returns the authenticated user's profile.
 * @param {string} token - Bearer access token
 * @returns {Promise<object>}
 */
export async function fetchUserProfile(token) {
  const response = await fetch(`${BASE_URL}/v2/users/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user profile');
  }

  const resBody = await response.json();
  return resBody.data;
}

/**
 * Logs out the user, clears local session data, and redirects to home.
 * Network failures are non-blocking.
 * @param {string} accessToken
 * @param {string} refreshToken
 */
export async function logout(accessToken, refreshToken) {
  try {
    await fetch(`${BASE_URL}/v2/users/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: `Bearer ${refreshToken}` }),
    });
  } catch {
    // Logout errors are intentionally swallowed — session is cleared regardless
  } finally {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_refresh_token');
    localStorage.removeItem('staff_profile');
    window.location.href = '/';
  }
}

/**
 * Uploads one or more files to NocoBase.
 * @param {File[]} files
 * @returns {Promise<Array<{id: string|number, url: string, name: string}>>}
 */
export async function uploadNocobaseAttachments(files) {
  if (!files || files.length === 0) return [];

  // Map each file to a fetch request
  const uploadPromises = files.map(async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const NOCOBASE_URL = `${NOCOBASE_BASE_URL}/api/attachments:create`;
    const response = await fetch(NOCOBASE_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${NOCOBASE_TOKEN}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to upload attachment');
    }

    const json = await response.json();
    return {
      id: json.data?.id,
      url: json.data?.url,
      name: file.name
    };
  });

  return Promise.all(uploadPromises);
}

/**
 * Submits a support ticket via the Template Management Service.
 * @param {object} data - Payload built by the form's buildSubmissionPayload()
 * @returns {Promise<any>}
 */
export async function submitTicket(data) {
  return apiClient('/v1/template-mgt/templates/send', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Submits a tech support ticket to Nocobase API.
 * @param {object} payload - The nocobase payload
 * @returns {Promise<any>}
 */
export async function submitNocobaseTicket(payload) {
  const NOCOBASE_URL = `${NOCOBASE_BASE_URL}/api/tickets:create`;
  
  const response = await fetch(NOCOBASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${NOCOBASE_TOKEN}`
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let errorMsg = `Nocobase API Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMsg = errorData.message || errorMsg;
    } catch (e) {}
    throw new Error(errorMsg);
  }

  return response.json();
}

/**
 * Fetches the list of impacted modules/platforms from the Nocobase API.
 * Returns an array of module name strings extracted from the response.
 * @param {number} [pageSize=50] - Number of records per page (50 covers all ~28 modules)
 * @returns {Promise<string[]>} Array of module name strings
 */
export async function fetchImpactedModules(pageSize = 50) {
  const url = `${NOCOBASE_BASE_URL}/api/impactedModules:list?page=1&pageSize=${pageSize}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${NOCOBASE_TOKEN}`,
    },
  });

  if (!response.ok) {
    let errorMsg = `Failed to fetch impacted modules: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMsg = errorData.message || errorMsg;
    } catch (e) {}
    throw new Error(errorMsg);
  }

  const json = await response.json();
  const items = json?.data ?? [];
  return items.map((item) => item.module_name ?? item.name ?? '').filter(Boolean);
}
